import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import DataDropContainer from 'features/analytics/compontents/DataDropContainer'
import FilesDropContainer from 'features/analytics/compontents/FilesDropContainer'
import PrimaryButton from 'components/atoms/PrimaryButton'
import Modal from 'components/atoms/Modal'
import Input from 'components/atoms/Input'
import FullWidthButton from 'features/auth/components/FullWidthButton'
import { isEmpty, values } from 'ramda'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  duplicateAnalyticsTemplateRoutine,
  updateAnalyticsTemplateRoutine
} from 'features/analytics/ducks/actions'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import { toast } from 'react-toastify'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import EditableIcon from '@mui/icons-material/Edit'
import NonEditableIcon from '@mui/icons-material/EditOff'
import { selectIsLoadingFileTags, selectIsLoadingFileTypes } from 'ducks/files/selectors'
import { getFileTagsForBpRoutine, getFileTypesRoutine } from 'ducks/files/actions'
import { TEMPLATE_ITEMS_NAMES } from 'utils/constants'

const EditTemplateForm = ({ isLoading, template, openAttachToAllModal, openRemoveModal, openShareModal, initialOpen, id }) => {
  const [open, setOpen] = useState(initialOpen)
  const [anchorEl, setAnchorEl] = useState(null)
  const [dataValues, setDataValues] = useState({
    hasUprn: false,
    hasAddress: false,
    hasBuildingTypes: false,
    hasOwner: false,
    hasAgent: false
  })
  const [fileTypesValues, setFileTypesValues] = useState([])
  const dispatch = useDispatch()
  const isLoadingFileTypes = useSelector(selectIsLoadingFileTypes)
  const isLoadingFileTags = useSelector(selectIsLoadingFileTags)
  const currentUser = useSelector(getCurrentUser)
  const [name, setName] = useState({
    main: 'New template',
    temp: 'New template'
  })
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [checklistItems, setChecklistItems] = useState([])
  const isDefaultTemplate = template.type === 'default'
  const isLocked = !!(template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked)
  const creatorId = template?.creator_id
    ?? template?.creatorId
    ?? template?.creator?.id
    ?? template?.created_by?.id
    ?? template?.createdBy?.id
    ?? template?.user_id
    ?? template?.userId
  const isCreator = creatorId && currentUser?.id
    ? String(creatorId) === String(currentUser.id)
    : false
  const isAdmin = currentUser?.type === 'admin'
  const serverCanUpdate = template?.canUpdate ?? template?.can_update
  const canEditByLocalState = isCreator || isAdmin || !isLocked
  const canEdit = typeof serverCanUpdate === 'boolean'
    ? serverCanUpdate
    : canEditByLocalState
  const isTemplateEditable = !isDefaultTemplate && canEdit
  const isReadOnly = isDefaultTemplate || !canEdit
  const readOnlyReason = isDefaultTemplate
    ? 'Default checklist is read-only.'
    : isLocked
      ? 'This template is set as non-editable by the creator.'
      : 'You do not have permission to edit this template.'
  const dropdownOpen = Boolean(anchorEl)
  const [lockModalOpen, setLockModalOpen] = useState(false)
  const selectedBpId = useSelector(state => state.bpDetails?.selectedBp?.id)
  const buildingPassportId = template?.buildingPassportId || template?.building_passport_id || selectedBpId
  const reorderItems = items => items.map((item, index) => ({ ...item, sortOrder: index }))
  const isMetadataLoading = isLoadingFileTypes || (Boolean(buildingPassportId) && isLoadingFileTags)

  const openMenu = e => {
    setAnchorEl(e.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const openNameModal = () => setNameModalOpen(true)
  const closeNameModal = () => {
    setNameModalOpen(false)
    setName(prev => ({ ...prev, temp: name.main }))
  }

  useEffect(() => {
    setDataValues({
      hasUprn: template.hasUprn,
      hasAddress: template.hasAddress,
      hasBuildingTypes: template.hasBuildingTypes,
      hasOwner: template.hasOwner,
      hasAgent: template.hasAgent
    })
    setFileTypesValues(template.fileTypes)
    setName({
      main: template.name,
      temp: template.name
    })
    const normalizedItems = (template.items || []).map((item, index) => ({
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      itemType: item.itemType || item.item_type || 'file',
      fileTypeId: item.fileTypeId || item.file_type_id || '',
      completionMode: item.completionMode || item.completion_mode || 'creator',
      sortOrder: item.sortOrder ?? item.sort_order ?? index,
      requiresUpload: item.requiresUpload ?? item.requires_upload ?? true,
      sourceType: (item.itemType || item.item_type || 'file') === 'file' ? 'file-library' : 'data-library',
      sourceKey: (item.itemType || item.item_type || 'file') === 'file'
        ? (item.fileTypeId || item.file_type_id || '')
        : null,
      tagRequirements: (item.tagRequirements || item.tag_requirements || []).map(tag => ({
        id: tag.id,
        fileTagId: tag.fileTagId || tag.file_tag_id || tag.file_tag?.id || '',
        tagKey: tag.tagKey || tag.tag_key || '',
        tagValue: tag.tagValue || tag.tag_value || '',
        operator: tag.operator || 'equals',
        groupNo: tag.groupNo ?? tag.group_no ?? 0
      }))
    }))
    setChecklistItems(normalizedItems)
  }, [template])

  useEffect(() => {
    dispatch(getFileTypesRoutine())
  }, [dispatch])

  useEffect(() => {
    if (buildingPassportId) {
      dispatch(getFileTagsForBpRoutine({ bpId: buildingPassportId }))
    }
  }, [dispatch, buildingPassportId])

  const handleSubmit = () => {
    if (!isTemplateEditable) {
      toast.info(readOnlyReason)
      return
    }
    const preparedItems = checklistItems
      .map((item, index) => ({
        ...(item.id ? { id: item.id } : {}),
        title: (item.title || '').trim(),
        description: (item.description || '').trim() || null,
        itemType: item.itemType || 'file',
        fileTypeId: item.itemType === 'file' ? (item.fileTypeId || null) : null,
        completionMode: item.completionMode || 'creator',
        sortOrder: item.sortOrder ?? index,
        requiresUpload: item.requiresUpload !== false,
        tagRequirements: (item.tagRequirements || [])
          .map(tag => ({
            ...(tag.id ? { id: tag.id } : {}),
            fileTagId: tag.fileTagId || null,
            tagKey: (tag.tagKey || '').trim() || null,
            tagValue: (tag.tagValue || '').trim() || null,
            operator: tag.operator || 'equals',
            groupNo: Number.isInteger(tag.groupNo) ? tag.groupNo : 0
          }))
          .filter(tag => tag.fileTagId || (tag.tagKey && tag.tagValue))
      }))
      .filter(item => item.title.length > 0)

    dispatch(updateAnalyticsTemplateRoutine({
      id: template.id,
      fileTypes: fileTypesValues.map(v => v.id),
      items: preparedItems,
      ...dataValues,
      ...(buildingPassportId ? { building_passport_id: buildingPassportId } : {})
    }))
  }

  const handleTitleChange = () => {
    if (!isTemplateEditable) {
      toast.info(readOnlyReason)
      return
    }

    dispatch(updateAnalyticsTemplateRoutine({
      id: template.id,
      name: name.temp,
      ...(buildingPassportId ? { building_passport_id: buildingPassportId } : {})
    }))
  }

  const shouldDisabledButton = () => {
    const hasChecklistItems = checklistItems.some(item => (item.title || '').trim().length > 0)
    return values(dataValues).every(val => val === false) && isEmpty(fileTypesValues) && !hasChecklistItems
  }

  const countGivenFields = useMemo(() => {
    return {
      data: values(dataValues).filter(v => v === true).length,
      files: fileTypesValues.length
    }
  }, [dataValues, fileTypesValues])

  const handleDuplicateTemple = () => {
    dispatch(duplicateAnalyticsTemplateRoutine({
      id: template.id
    }))
    setAnchorEl(null)
  }

  const handleRemoveModalOpen = () => {
    openRemoveModal(template)
    setAnchorEl(null)
  }

  const handleAttachToAllModalOpen = () => {
    openAttachToAllModal(template)
    setAnchorEl(null)
  }

  const handleDataItemDropped = item => {
    const sourceKey = item?.value
    if (!sourceKey) return

    setChecklistItems(prev => {
      const alreadyExists = prev.some(checklistItem => checklistItem.sourceType === 'data-library' && checklistItem.sourceKey === sourceKey)
      if (alreadyExists) return prev

      return reorderItems([
        ...prev,
        {
          id: undefined,
          title: item.label || TEMPLATE_ITEMS_NAMES[sourceKey] || '',
          description: '',
          itemType: 'data',
          fileTypeId: '',
          completionMode: 'creator',
          requiresUpload: false,
          sourceType: 'data-library',
          sourceKey,
          tagRequirements: []
        }
      ])
    })
  }

  const handleDataItemRemoved = sourceKey => {
    setChecklistItems(prev => reorderItems(
      prev.filter(item => !(item.sourceType === 'data-library' && item.sourceKey === sourceKey))
    ))
  }

  const handleFileItemDropped = fileType => {
    if (!fileType?.id) return

    setChecklistItems(prev => reorderItems([
      ...prev,
      {
        id: undefined,
        title: fileType.name || '',
        description: '',
        itemType: 'file',
        fileTypeId: fileType.id,
        completionMode: 'creator',
        requiresUpload: true,
        sourceType: 'file-library',
        sourceKey: fileType.id,
        tagRequirements: []
      }
    ]))
  }

  const handleFileItemRemoved = fileTypeId => {
    setChecklistItems(prev => reorderItems(
      prev.filter(item => !(item.sourceType === 'file-library' && String(item.fileTypeId) === String(fileTypeId)))
    ))
  }

  const getSelectedTagsForFile = fileTypeId => {
    const fileItem = checklistItems.find(item => item.sourceType === 'file-library' && String(item.fileTypeId) === String(fileTypeId))
    if (!fileItem) return []
    return (fileItem.tagRequirements || [])
      .map(tag => tag.fileTagId)
      .filter(Boolean)
  }

  const handleFileTagsChange = (fileTypeId, tagIds) => {
    setChecklistItems(prev => prev.map(item => {
      if (!(item.sourceType === 'file-library' && String(item.fileTypeId) === String(fileTypeId))) {
        return item
      }
      return {
        ...item,
        tagRequirements: (tagIds || []).map(tagId => ({
          fileTagId: tagId,
          operator: 'equals',
          groupNo: 0
        }))
      }
    }))
  }

  return (
    <PageWrapper id={id}>
      <FormContent>
        <Header open={open}>
          <Title>
            <div>
              {name.main}
              <TemplateType>{template.type}</TemplateType>
            </div>
            {!isDefaultTemplate && isTemplateEditable && <EditIcon onClick={openNameModal} />}
          </Title>
          <OptionsWrapper>
            <TagsWrapper>
              {isDefaultTemplate && <Tag>UNEDITABLE</Tag>}
              <Tag>
                {countGivenFields.data} data field{countGivenFields.data === 1 ? '' : 's'},&nbsp;
                {countGivenFields.files} file{countGivenFields.files === 1 ? '' : 's'}
              </Tag>
            </TagsWrapper>
            {isReadOnly && (
              <ReadOnlyNotice>
                {isDefaultTemplate
                  ? 'Read-only template'
                  : isLocked
                    ? 'Non-editable template'
                    : 'View-only (no edit permission)'}
              </ReadOnlyNotice>
            )}
            <IconWrapper>
              <MoreIcon disabled={isLoading} onClick={openMenu} />
              <Menu
                keepMounted
                disabled={isLoading}
                anchorEl={anchorEl}
                style={{
                  marginTop: '10px'
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                open={dropdownOpen}
                onClose={handleClose}
              >
                <MenuItem onClick={handleDuplicateTemple}>Duplicate</MenuItem>
                {!isDefaultTemplate && (
                  <MenuItem onClick={openShareModal(template)}>Share template</MenuItem>
                )}
                {!isDefaultTemplate && <MenuItem onClick={handleAttachToAllModalOpen}>Attach to all Building Passports</MenuItem>}
                {!isDefaultTemplate && (isCreator || isAdmin) && (
                  <MenuItem onClick={() => setLockModalOpen(true)}>
                    {isLocked ? 'Make editable' : 'Make non-editable'}
                  </MenuItem>
                )}
                {/* Creator/admin can always delete their template, even when it is locked (non-editable) */}
                {!isDefaultTemplate && (isCreator || isAdmin) && <MenuItem onClick={handleRemoveModalOpen}>Remove</MenuItem>}
              </Menu>
            </IconWrapper>
            <CollapseIcon
              onClick={() => setOpen(prev => !prev)}
              open={open}
            />
          </OptionsWrapper>
        </Header>
        <Collapse in={open}>
          <DataDropContainer
            isEdit
            isDefaultTemplate={isDefaultTemplate}
            isEditable={isTemplateEditable}
            isLocked={isLocked}
            readOnlyReason={readOnlyReason}
            dataValues={dataValues}
            setDataValues={setDataValues}
            onDataItemDropped={handleDataItemDropped}
            onDataItemRemoved={handleDataItemRemoved}
          />
          {
            isEmpty(fileTypesValues) && isDefaultTemplate
              ? null
              : <FilesDropContainer
                  isEdit
                  isDefaultTemplate={isDefaultTemplate}
                  isEditable={isTemplateEditable}
                  isLocked={isLocked}
                  readOnlyReason={readOnlyReason}
                  fileTypesValues={fileTypesValues}
                  setFileTypesValues={setFileTypesValues}
                  onFileItemDropped={handleFileItemDropped}
                  onFileItemRemoved={handleFileItemRemoved}
                  getSelectedTagsForFile={getSelectedTagsForFile}
                  onFileTagsChange={handleFileTagsChange}
                  isMetaLoading={isMetadataLoading}
                />
          }
          <ButtonWrapper>
            {!isDefaultTemplate && isTemplateEditable && (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={shouldDisabledButton()}
              >
                Save changes
              </PrimaryButton>
            )}
          </ButtonWrapper>
        </Collapse>
      </FormContent>
      <Modal
        open={nameModalOpen}
        onClose={closeNameModal}
        title='Set template name'
      >
        <Input
          name='name'
          value={name.temp}
          onChange={(_, value) => setName(prev => ({ ...prev, temp: value }))}
        />
        <FullWidthButton
          disabled={isEmpty(name.temp)}
          onClick={() => {
            setName(prev => ({ ...prev, main: name.temp }))
            setNameModalOpen(false)
            handleTitleChange()
          }}
        >
          Save
        </FullWidthButton>
      </Modal>

      <Modal
        open={lockModalOpen}
        onClose={() => setLockModalOpen(false)}
        title={isLocked ? 'Make template editable' : 'Make template non-editable'}
        withCancel
        withSubmit
        submitButtonText="Save"
        cancelButtonText="Cancel"
        onCancel={() => setLockModalOpen(false)}
        onSubmit={() => {
          dispatch(updateAnalyticsTemplateRoutine({
            id: template.id,
            locked: !isLocked
          }))
          setLockModalOpen(false)
        }}
      >
        <LockModalContent>
          <LockModalLabel>
            {isLocked ? <EditableIcon fontSize="small" /> : <NonEditableIcon fontSize="small" />}
            {isLocked ? 'Make this template editable?' : 'Make this template non-editable?'}
          </LockModalLabel>
        <LockModalHint>
          {isLocked
            ? 'Shared users will be able to edit once set as editable.'
            : 'Shared users will not be able to edit when set as non-editable.'}
        </LockModalHint>
        {!isCreator && !isAdmin && (
          <LockModalNote>Only the template creator can change lock status.</LockModalNote>
        )}
      </LockModalContent>
    </Modal>
    </PageWrapper>
  )
}

export default EditTemplateForm

const PageWrapper = styled.div`
  box-sizing: border-box;
  margin-bottom: 20px;
  width: 100%;
`

const FormContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  padding: 20px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  transition: all .3s;
  width: 100%;
  margin-bottom: ${({ open }) => open ? '20px' : '0'};
`

const Title = styled.div`
  display: flex;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`

const EditIcon = styled(EditOutlinedIcon)`
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all .3s;
  margin-left: 10px;

  &:hover {
    opacity: .8;
  }
`

const TemplateType = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
  text-transform: uppercase;
  font-weight: normal;
`

const TagsWrapper = styled.div`
  display: flex;
  margin-right: 15px;
`

const OptionsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Tag = styled.div`
  padding: 5px 10px;
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  color: ${({ theme }) => theme.colors.darkGrey};
  border-radius: 4px;
  font-size: 13px;
  font-weight: lighter;
  margin-left: 10px;
`

const ReadOnlyNotice = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #d14724;
  margin-right: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const LockModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LockModalLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const LockModalHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const LockModalNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
`

const CollapseIcon = styled(KeyboardArrowDownOutlinedIcon)`
  transition: all .3s;
  cursor: pointer;
  transform: rotate(${({ open }) => open ? '180deg': '0'});
`

const MoreIcon = styled(MoreVertIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  font-size: 20px !important;

  ${({ disabled }) => disabled && css`
    cursor: not-allowed !important;
    pointer-events: none !important;
  `}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`
